import { readFileSync, existsSync } from 'fs';
import type { FrameworkIntegration, DetectionResult, IntegrationConfig } from './types.js';

interface PackageJson {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}

export const openaiIntegration: FrameworkIntegration = {
  name: 'openai',
  description: 'OpenAI SDK integration with automatic tracing',

  detect(): Promise<DetectionResult> {
    let hasOpenAI = false;

    if (existsSync('package.json')) {
      try {
        const pkg = JSON.parse(readFileSync('package.json', 'utf-8')) as PackageJson;
        const deps: Record<string, string> = { ...pkg.dependencies, ...pkg.devDependencies };
        hasOpenAI = 'openai' in deps;
      } catch {
        // Ignore
      }
    }

    return Promise.resolve({
      detected: hasOpenAI,
      confidence: hasOpenAI ? 0.85 : 0,
      files: hasOpenAI ? ['package.json'] : [],
    });
  },

  generate(config: IntegrationConfig): Promise<string> {
    return Promise.resolve(`---
name: mutagent-openai-integration
description: Integrate MutagenT with OpenAI SDK for automatic tracing
framework: openai
version: 2.0.0
---

# MutagenT + OpenAI SDK Integration

## Prerequisites Verification

- MUTAGENT_API_KEY: ${config.apiKey.slice(0, 8)}...${config.apiKey.slice(-4)}
- MUTAGENT_ENDPOINT: ${config.endpoint}
- API Connection: Verified

## Installation

\`\`\`bash
bun add @mutagent/openai @mutagent/sdk
# or
npm install @mutagent/openai @mutagent/sdk
\`\`\`

## Integration

\`\`\`typescript
import { observeOpenAI } from '@mutagent/openai';
import { initTracing } from '@mutagent/sdk/tracing';

// Initialize MutagenT tracing
initTracing({ apiKey: process.env.MUTAGENT_API_KEY! });

// Wrap your OpenAI client with observeOpenAI for automatic tracing
const openai = observeOpenAI(new OpenAI({
  apiKey: process.env.OPENAI_API_KEY,
}));

// All calls are automatically traced
const response = await openai.chat.completions.create({
  model: 'gpt-4o',
  messages: [{ role: 'user', content: 'Hello!' }],
});

console.log(response.choices[0].message.content);
\`\`\`

## Streaming

Streaming works out of the box with no extra configuration:

\`\`\`typescript
const stream = await openai.chat.completions.create({
  model: 'gpt-4o',
  messages: [{ role: 'user', content: 'Tell me a story' }],
  stream: true,
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content || '');
}
\`\`\`

## Embeddings

\`\`\`typescript
const embedding = await openai.embeddings.create({
  model: 'text-embedding-3-small',
  input: 'Hello world',
});
// Embedding call is traced automatically
\`\`\`

## Verification

\`\`\`bash
mutagent integrate openai --verify
\`\`\`

## Documentation

- Full guide: https://docs.mutagent.io/integrations/openai
- API Reference: https://docs.mutagent.io/sdk/tracing
- Dashboard: https://app.mutagent.io
`);
  },
};
